import React, {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Column,
  Columns,
  Header,
  NumberInput,
  Section,
  Table,
  TableBody,
  TableBodyCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { round, subtract } from 'vend-number'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import { getCurrencyFormatParts } from '@vend/utilities'
import { PAYMENT_TYPES } from '@vend/business-components'
import { useLocalizedPaymentTypeName } from '@vend/react-utilities'
import {
  ClosurePayment,
  DenominationQuantity,
  RegisterClosure,
} from '../../util/register-closure-types'
import {
  isCashConcealedPaymentType,
  isReadOnlyPaymentType,
} from '../../functions/payments'
import {
  usePaymentManager,
  useRegisterClosureManager,
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../ng-compat/hooks'
import {
  RegisterClosureSummaryType,
  useRegisterClosure,
} from './useRegisterClosure'
import { RegisterClosureSummaryReport } from './RegisterClosureSummaryReport'
import { RegisterDetails } from './RegisterDetails'
import {
  DECIMAL_PLACES,
  NOT_APPLICABLE,
  useFormatClosureNumber,
} from './useFormatClosureNumber'
import { getTotalCounted } from './getTotalCounted'
import { RegisterClosureCashCounting } from './RegisterClosureCashCounting/RegisterClosureCashCounting'
import { filterPaymentTypes } from './filterPaymentTypes'

export interface RowCount {
  isCashManagement: boolean
  count: string
  error: boolean
}

export interface ClosureTotals {
  expectedTotal: string
  countedTotal: string
  differenceTotal: string
  countedValid: boolean
}

interface RegisterClosureSummaryProps {
  type: RegisterClosureSummaryType
  counts?: Record<string, RowCount>
  setCount?: (paymentTypeId: string, count: string) => void
  setError?: (paymentTypeId: string, error: boolean) => void
  onDenominationChanged?: (denominations: DenominationQuantity[]) => void
  totals: ClosureTotals
}

export const RegisterClosureSummary: FunctionComponent<RegisterClosureSummaryProps> = ({
  type,
  counts,
  setCount,
  setError,
  totals,
  onDenominationChanged,
}) => {
  const t = useT()
  const localizedPaymentTypeName = useLocalizedPaymentTypeName()
  const summary = useRegisterClosure({ type })
  const registerClosureManager = useRegisterClosureManager()
  const paymentManager = usePaymentManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const activeOutlet = useRegisterManager().getActiveOutlet()
  const formatClosureNumber = useFormatClosureNumber()
  const [currencySymbol, setCurrencySymbol] = useState('')
  const [rowsExpanded, setRowsExpanded] = useState<Record<string, boolean>>({})

  const retailer = retailerSettingsManager.get()
  const decimalSeparator = useMemo(
    () =>
      getCurrencyFormatParts(
        retailer.culture,
        activeOutlet.currency,
        activeOutlet.currency_symbol
      ).decimal,
    [activeOutlet, retailer]
  )

  useEffect(() => {
    retailerSettingsManager.init().then(() => {
      const retailer = retailerSettingsManager.get()
      if (!retailer) {
        return
      }
      setCurrencySymbol(retailer.currency.symbol)
    })
  }, [retailerSettingsManager])

  const isRowExpandable = useCallback(
    (payment: ClosurePayment) =>
      paymentManager.isCashManagementPaymentType(payment) &&
      !isCashConcealedPaymentType(payment),
    [paymentManager]
  )

  const toggleRowExpanded = useCallback(
    (payment: ClosurePayment) => {
      setRowsExpanded({
        ...rowsExpanded,
        [payment.payment_type_id]: !rowsExpanded[payment.payment_type_id],
      })
    },
    [rowsExpanded]
  )

  const isRowEditable = (payment: ClosurePayment) =>
    !isReadOnlyPaymentType(payment)

  useEffect(() => {
    const createExpandedRows = () => {
      if (!summary) {
        return
      }
      let hasChange = false
      const newExpandedRows = { ...rowsExpanded }

      summary.payments.forEach(payment => {
        if (newExpandedRows[payment.payment_type_id] !== undefined) {
          return
        }

        if (isRowExpandable(payment)) {
          hasChange = true
          newExpandedRows[payment.payment_type_id] = true
        }
      })

      if (hasChange) {
        setRowsExpanded(newExpandedRows)
      }
    }

    createExpandedRows()
  }, [isRowExpandable, paymentManager, rowsExpanded, summary])

  const filteredPayments = useMemo(() => {
    if (!summary) {
      return []
    }
    return filterPaymentTypes(summary.payments, summary.outlet.id)
  }, [summary])

  const differenceBetweenExpectedAndCounted = (
    payment: ClosurePayment,
    summary: RegisterClosure,
    type: RegisterClosureSummaryType
  ) => {
    const currentCount =
      type === 'current'
        ? counts?.[payment.payment_type_id]?.count || ''
        : getTotalCounted(payment)
    if (isEmpty(currentCount) || isCashConcealedPaymentType(payment)) {
      return NOT_APPLICABLE
    }

    if (
      type === 'last' &&
      paymentManager.isCashManagementPaymentType(payment)
    ) {
      return subtract(
        round(summary.countedCashInDrawer, DECIMAL_PLACES),
        round(
          registerClosureManager.determineExpectedPaymentTotal(
            payment,
            summary
          ),
          DECIMAL_PLACES
        )
      )
    }

    const difference = subtract(
      round(currentCount, DECIMAL_PLACES),
      round(
        registerClosureManager.determineExpectedPaymentTotal(payment, summary),
        DECIMAL_PLACES
      )
    )

    return difference
  }

  const cashPayment = useMemo(() => {
    if (summary == null) {
      return null
    }
    const cashPayments = filteredPayments?.filter(
      payment => payment.paymentType.type_id === PAYMENT_TYPES.CASH
    )

    if (cashPayments?.length === 0) {
      return null
    }

    return cashPayments[0]
  }, [summary, filteredPayments])

  if (!summary) {
    return null
  }

  // Only show cash counting if cash is present and summary type is current
  const shouldShowCashCounting = cashPayment && type === 'current'

  return (
    <Section>
      <Columns space={10} collapseBelow="tablet">
        <Column width="3/12">
          <Header level="2" type="sub-heading">
            {type === 'current' ? (
              <T _str="Register Details" />
            ) : (
              <T _str="Last closure details" />
            )}
          </Header>
        </Column>
        <Column width="9/12" className="register-closure-info-fields">
          <RegisterDetails summary={summary} />
        </Column>
      </Columns>

      <hr className="vd-hr" />
      {shouldShowCashCounting && (
        <>
          <Columns space={10} collapseBelow="tablet">
            <Column width="3/12">
              <Header level="2" type="sub-heading" className="vd-mb5">
                <T _str="Count cash" />
              </Header>
              <T _str="Enter the amount from the till." />
            </Column>
            <Column width="4/12">
              {(() => {
                const currencyCode = summary.outlet?.currency || 'USD'
                return (
                  <RegisterClosureCashCounting
                    closureSessionKey={summary.register_open_time}
                    currencyCode={currencyCode}
                    paymentName={cashPayment.payment_type_name}
                    value={
                      counts?.[cashPayment.payment_type_id]?.count || '0.00'
                    }
                    onChange={val =>
                      setCount?.(cashPayment.payment_type_id, val)
                    }
                    onDenominationChanged={denominationsUsed => {
                      if (onDenominationChanged) {
                        onDenominationChanged(denominationsUsed)
                      }
                    }}
                  />
                )
              })()}
            </Column>
          </Columns>
          <hr className="vd-hr" />
        </>
      )}

      <Header level="2" type="sub-heading" className="vd-mb5">
        {type === 'current' ? (
          <T _str="Payments Summary" />
        ) : (
          <T _str="Last payments summary" />
        )}
      </Header>
      <Columns space={10} collapseBelow="tablet">
        <Column width="3/12">
          {type === 'current' ? (
            <T _str="Balance your register by entering the amount counted from the till and other payment terminals into the empty fields here." />
          ) : (
            ' '
          )}
        </Column>
        <Column width="9/12" className="register-closure-info-fields">
          <Table className="wr-closure-summary-table vd-table vd-table--compact">
            <TableHead>
              <TableRow>
                <TableHeadCell className="vd-pl2">
                  <T _str="Payment types" />
                </TableHeadCell>
                <TableHeadCell className="wr-closure-summary-table-column vd-align-right">
                  <T
                    _str="Expected ({currencySymbol})"
                    currencySymbol={currencySymbol}
                  />
                </TableHeadCell>
                <TableHeadCell className="wr-closure-summary-table-column vd-align-right">
                  <T
                    _str="Counted ({currencySymbol})"
                    currencySymbol={currencySymbol}
                  />
                </TableHeadCell>
                <TableHeadCell className="wr-closure-summary-table-column vd-align-right">
                  <T
                    _str="Differences ({currencySymbol})"
                    currencySymbol={currencySymbol}
                  />
                </TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.map(payment => (
                <Fragment key={payment.payment_type_id}>
                  <TableRow
                    key={`${payment.payment_type_name}-${payment.payment_type_id}`}
                    className={classNames('wr-closure-summary-table-row', {
                      'vd-expandable': isRowExpandable(payment),
                      'vd-expandable--expanded':
                        rowsExpanded[payment.payment_type_id],
                    })}
                    onClick={
                      isRowExpandable(payment)
                        ? () => {
                            toggleRowExpanded(payment)
                          }
                        : undefined
                    }
                  >
                    <TableBodyCell className="vd-pl2">
                      {isRowExpandable(payment) ? (
                        <div className="wr-cash-management-row-title">
                          <div className="vd-pr1">
                            {localizedPaymentTypeName(
                              payment.payment_type_name
                            )}
                          </div>
                          <div className="wr-cash-management-row-title vd-text--sub vd-text--secondary">
                            <div className="vd-pr1">
                              <T _str="View cash payments, floats and movements" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        localizedPaymentTypeName(payment.payment_type_name)
                      )}
                    </TableBodyCell>
                    <TableBodyCell className="vd-align-right wr-closure-summary-table-expected-field">
                      {formatClosureNumber(
                        registerClosureManager.determineExpectedPaymentTotal(
                          payment,
                          summary
                        )
                      )}
                    </TableBodyCell>
                    <TableBodyCell className="vd-align-right wr-closure-summary-table-counted-field">
                      {counts && setCount && isRowEditable(payment) ? (
                        <div className="wr-closure-summary-table-input-field">
                          <NumberInput
                            className="wr-closure-summary-table-input"
                            placeholder={t('Enter amount')}
                            decimalSeparator={decimalSeparator}
                            value={counts[payment.payment_type_id]?.count}
                            onChange={val => {
                              setCount(payment.payment_type_id, val.toString())
                            }}
                            onBlur={() => {
                              if (counts[payment.payment_type_id].count) {
                                setCount(
                                  payment.payment_type_id,
                                  round(
                                    counts[payment.payment_type_id].count,
                                    DECIMAL_PLACES
                                  )
                                )
                                if (
                                  setError &&
                                  counts[payment.payment_type_id].error
                                ) {
                                  setError(payment.payment_type_id, false)
                                }
                              }
                            }}
                            onError={() => {
                              if (setError) {
                                setError(payment.payment_type_id, true)
                              }
                            }}
                            error={
                              counts[payment.payment_type_id]?.error
                                ? t('Invalid amount', {
                                    _context:
                                      'Generic error message for invalid register closure fields',
                                  })
                                : undefined
                            }
                            selectTextOnClick
                          />
                        </div>
                      ) : (
                        formatClosureNumber(
                          paymentManager.isCashManagementPaymentType(payment)
                            ? summary.countedCashInDrawer
                            : getTotalCounted(payment)
                        )
                      )}
                    </TableBodyCell>
                    <TableBodyCell className="vd-align-right wr-closure-summary-table-differences-field">
                      {formatClosureNumber(
                        differenceBetweenExpectedAndCounted(
                          payment,
                          summary,
                          type
                        )
                      )}
                    </TableBodyCell>
                  </TableRow>
                  {isRowExpandable(payment) ? (
                    <TableRow
                      key={`detail-${payment.payment_type_id}`}
                      className="vd-table-nested-report-container"
                    >
                      <TableBodyCell colSpan={4}>
                        <RegisterClosureSummaryReport
                          closure={summary}
                          countedCashInDrawer={
                            type === 'current'
                              ? counts?.[payment.payment_type_id]?.count || ''
                              : summary.countedCashInDrawer
                          }
                          currencySymbol={currencySymbol}
                        />
                      </TableBodyCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))}
            </TableBody>
            <tfoot>
              <TableRow className="wr-closure-summary-totals">
                <TableBodyCell>
                  <T _str="Totals" />
                </TableBodyCell>
                <TableBodyCell
                  className="vd-align-right"
                  data-cy="expected-totals"
                >
                  {formatClosureNumber(totals.expectedTotal)}
                </TableBodyCell>
                <TableBodyCell
                  className="vd-align-right"
                  data-cy="counted-totals"
                >
                  {formatClosureNumber(totals.countedTotal)}
                </TableBodyCell>
                <TableBodyCell
                  className="vd-align-right"
                  data-cy="differences-totals"
                >
                  {formatClosureNumber(totals.differenceTotal)}
                </TableBodyCell>
              </TableRow>
            </tfoot>
          </Table>
        </Column>
      </Columns>
    </Section>
  )
}
